import React from "react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import Fill from "ol/style/Fill";
import Icon from "ol/style/Icon";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import Text from "ol/style/Text";
import CircleStyle from "ol/style/Circle";
import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import KML from "ol/format/KML.js";
import Draw from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import Translate from "ol/interaction/Translate";
import GeometryCollection from "ol/geom/GeometryCollection";
import LineString from "ol/geom/LineString";
import Polygon, { circular } from "ol/geom/Polygon";
import MultiPoint from "ol/geom/MultiPoint";
import Point from "ol/geom/Point";
import Layer from "ol/layer/Layer";
import BingMaps from "ol/source/BingMaps";
import Google from "ol/source/Google";
import TileArcGISRest from "ol/source/TileArcGISRest";
import TileJSON from "ol/source/TileJSON";
import XYZ from "ol/source/XYZ";

import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";

import { center, circle, convex, distance, lineString, point, points, buffer as turfBuffer } from "@turf/turf";
import { createEmpty, extend } from "ol/extent";

import { ConvertISOToDate, ConvertMetersToFeet, DateHasAlreadyPassed, GetVerticalSpeedIndicator, GetColorFromState } from "../util";
import { getOperationPriority } from "../manager/operations/opUtil";

export const Colors = {
    white: "rgb(255, 255, 255)",
    black: "rgb(0, 0, 0)",
    yellow: "rgb(255, 255, 0)",
    orange: "rgb(255, 165, 0)",
    lime: "rgb(0, 255, 0)",
    green: "rgb(0, 200, 0)",
    red: "rgb(255, 80, 80)",
    red2: "rgb(255, 0, 0)",
    blue1: "rgb(0, 135, 191)",
    blue2: "rgb(5, 142, 184)",
    blue3: "rgb(3, 136, 191)",
    blue4: "rgb(51, 153, 204)",
    lightBlue1: "rgb(144, 202, 249)",
    lightBlue2: "rgb(0, 225, 255)",
    pink1: "rgb(157, 107, 158)",
    pink2: "rgb(158, 110, 157)",
    lightPink: "rgb(216, 171, 198)"
};

export const LowOpacityColors = {
    yellow: "rgba(255, 255, 0, 0.1)",
    orange: "rgba(255, 165, 0, 0.1)",
    lime: "rgba(0, 255, 0, 0.1)",
    red2: "rgba(255, 0, 0, 0.1)",
    blue1: "rgba(0, 135, 191, 0.1)",
    blue2: "rgba(5, 142, 184, 0.1)",
    blue3: "rgba(3, 136, 191, 0.1)",
    pink1: "rgba(157, 107, 158, 0.1)",
    pink2: "rgba(158, 110, 157, 0.1)",
    lightPink: "rgba(216, 171, 198, 0.1)"
};

const LAANC_STYLE_DATA = {
    LAANC_AIRSPACE: {
        CLASS_B: {
            fillColor: LowOpacityColors.blue1,
            strokeColor: Colors.blue1
        },
        CLASS_C: {
            fillColor: LowOpacityColors.pink1,
            strokeColor: Colors.pink1
        },
        CLASS_D: {
            fillColor: LowOpacityColors.blue2,
            strokeColor: Colors.blue2,
            strokeLineDash: [8, 4]
        },
        CLASS_E: {
            fillColor: LowOpacityColors.pink2,
            strokeColor: Colors.pink2,
            strokeLineDash: [8, 4]
        },
        MODE_C: {
            fillColor: LowOpacityColors.pink2,
            strokeColor: Colors.pink2
        }
    },
    UASFM: (laancApt1, ceiling) => {
        const opacity = Math.max(0, 0.6 - 0.075 * Math.floor(ceiling / 50));
        return {
            fillColor: laancApt1 === 1 ? `rgba(144, 202, 249, ${opacity})` : `rgba(255, 80, 80, ${opacity})`,
            strokeColor: laancApt1 === 1 ? Colors.lightBlue1 : Colors.red,
            textLabel: `${ceiling}ft`
        };
    },
    NSUFR: {
        fillColor: LowOpacityColors.lightPink,
        strokeColor: Colors.lightPink,
        strokeLineDash: [8, 4]
    },
    SU_AIRSPACE: {
        A: {
            fillColor: LowOpacityColors.blue3,
            strokeColor: Colors.blue3,
            strokeLineDash: [1, 2]
        },
        MOA: {
            fillColor: LowOpacityColors.pink1,
            strokeColor: Colors.pink1,
            strokeLineDash: [2, 4]
        },
        P: {
            fillColor: LowOpacityColors.blue3,
            strokeColor: Colors.blue3,
            strokeLineDash: [2, 4]
        },
        R: {
            fillColor: LowOpacityColors.blue3,
            strokeColor: Colors.blue3,
            strokeLineDash: [2, 4]
        },
        W: {
            fillColor: LowOpacityColors.blue3,
            strokeColor: Colors.blue3,
            strokeLineDash: [2, 4]
        }
    },
    LAANC_STADIUMS: (name) => ({
        fillColor: LowOpacityColors.lime,
        strokeColor: Colors.lime,
        textLabel: name
    }),
    FAA_TFR: (properties) => {
        let strokeColor = Colors.red2;
        let fillColor = LowOpacityColors.red2;

        try {
            const tfrActive = DateHasAlreadyPassed(properties.effectiveStart);
            const heightInfo = JSON.parse(properties.heightInformation);

            if (parseFloat(heightInfo.lowerLevel) > 400 && heightInfo.uomLowerLevel === "FT") {
                strokeColor = Colors.yellow;
                fillColor = LowOpacityColors.yellow;
            } else if (tfrActive === false) {
                strokeColor = Colors.orange;
                fillColor = LowOpacityColors.orange;
            }
        } catch (err) {
            console.error(err);
        }
        return {
            fillColor: fillColor,
            strokeColor: strokeColor
        };
    },
    AIRPORTS: (typeCode) => {
        let image = "";
        if (typeCode === "HP") {
            image = "../static/2d-models/airport/HelicopterIcon.svg";
        } else {
            image = "../static/2d-models/airport/AirplaneIcon.svg";
        }
        return {
            image: image
        };
    },
    DC_FRZ_SFRA: (sector) => {
        if (sector === "DC SFRA") {
            return {
                strokeColor: Colors.orange,
                fillColor: LowOpacityColors.orange
            };
        } else {
            return {
                strokeColor: Colors.red2,
                fillColor: LowOpacityColors.red2
            };
        }
    }
};

export const FILL_WHITE = new Fill({ color: "rgba(255, 255, 255, 0.4)" });
export const FILL_BLUE = new Fill({ color: "rgba(51, 153, 204, 0.4)" });
export const STROKE_WHITE = new Stroke({ color: "#FFFFFF", width: 1.25 });
export const STROKE_BLUE = new Stroke({ color: "#3399CC", width: 1.25 });

// Default style for volumes.
export const STYLE_VOLUME = new Style({
    fill: FILL_BLUE,
    stroke: STROKE_WHITE
});

// Default style for volumes currently being created/edited.
export function GetEditVolumeStyle(settings, colorHex) {
    if (!colorHex) {
        colorHex = settings.op_state_settings.find((op) => op.state === "PLANNING").color;
    }
    const colorRgba = convertHexColorToRgbaColor(colorHex, 0.4);
    return [
        new Style({
            fill: new Fill({ color: colorRgba }),
            stroke: new Stroke({ color: colorHex, width: 3.5 })
        }),
        new Style({
            image: new CircleStyle({
                fill: new Fill({ color: colorHex }),
                stroke: new Stroke({ color: colorHex, width: 1.25 }),
                radius: 5
            }),
            geometry: (feature) => {
                if (!feature || feature.get("edit") || feature.get("type") === "Circle" || !feature.getGeometry()) {
                    return;
                }

                const coords = feature.getGeometry().getCoordinates();
                if (!coords || coords.length === 0 || !Array.isArray(coords)) {
                    return;
                } else if (Array.isArray(coords[0])) {
                    return new MultiPoint(Array.isArray(coords[0][0]) ? coords[0] : coords);
                } else {
                    return new Point(coords);
                }
            }
        })
    ];
}

export function GetGeofenceStyle(settings) {
    const colorHex = settings.op_state_settings.find((op) => op.state === "PLANNING").color;
    const colorRgba = convertHexColorToRgbaColor(colorHex, 0.4);
    return new Style({
        fill: new Fill({ color: colorRgba }),
        stroke: new Stroke({ color: colorHex, width: 1 })
    });
}

// Additional style for assets currently being monitored.
export const STYLE_VOLUME_MONITOR = new Style({
    stroke: new Stroke({
        color: "red",
        width: 2,
        lineDash: [10, 10]
    }),
    fill: new Fill({
        color: LowOpacityColors.red2
    })
});

// Default style for ASD entities.
export const STYLE_ASD = new Style({
    image: new Icon({
        src: "../static/2d-models/adsb/adsb-default.png"
    })
});

// Gets the feature's style or the 1st style if it has multiple.
export function GetFirstStyle(feature) {
    const style = feature.getStyle();
    return Array.isArray(style) ? style[0] : style;
}

export const allModels = new Map();
allModels.set(0, "../static/2d-models/adsb/adsb-default.png");
allModels.set(1, "../static/2d-models/adsb/adsb-light.png");
allModels.set(2, "../static/2d-models/adsb/adsb-light.png");
allModels.set(3, "../static/2d-models/adsb/adsb-large.png");
allModels.set(4, "../static/2d-models/adsb/adsb-heavy.png");
allModels.set(5, "../static/2d-models/adsb/adsb-heavy.png");
allModels.set(7, "../static/2d-models/adsb/adsb-rotocraft.png");
allModels.set(8, "../static/2d-models/telemetry/telemetry-quad.png");

// Gets the LAANC volume's style based on its properties.
export function GetLaancStyle(feature) {
    const properties = feature.getProperties();
    const layer = properties.layer;

    let styleData;
    if (layer === "LAANC_AIRSPACE") {
        styleData = LAANC_STYLE_DATA[layer][properties.LOCAL_TYPE];
    } else if (layer.includes("UASFM")) {
        styleData = LAANC_STYLE_DATA["UASFM"](properties.APT1_LAANC, properties.CEILING);
    } else if (layer.includes("NSUFR")) {
        styleData = LAANC_STYLE_DATA["NSUFR"];
    } else if (layer === "SU_AIRSPACE") {
        styleData = LAANC_STYLE_DATA[layer][properties.TYPE_CODE];
    } else if (layer === "LAANC_STADIUMS") {
        styleData = LAANC_STYLE_DATA[layer](properties.NAME);
    } else if (layer === "FAA_TFR" && !DateHasAlreadyPassed(properties.effectiveEnd)) {
        styleData = LAANC_STYLE_DATA[layer](properties);
    } else if (layer === "AIRPORTS") {
        styleData = LAANC_STYLE_DATA[layer](properties.TYPE_CODE);
    } else if (layer === "DC_FRZ_SFRA") {
        styleData = LAANC_STYLE_DATA[layer](properties.SECTOR);
    } else if (layer === "SEAMS_STADIUMS") {
        styleData = LAANC_STYLE_DATA["LAANC_STADIUMS"](properties.NAME);
    }
    if (!styleData) {
        styleData = {
            fillColor: "rgba(0, 0, 0, 0)",
            strokeColor: "rgba(0, 0, 0, 0)",
            strokeLineDash: undefined,
            textLabel: ""
        };
    }
    if (!styleData.fillColor) styleData.fillColor = "rgba(0, 0, 0, 0)";
    if (!styleData.strokeColor) styleData.strokeColor = "rgba(0, 0, 0, 0)";
    if (!styleData.textLabel) styleData.textLabel = "";

    let text = undefined;
    if (styleData.textLabel) {
        text = new Text({
            text: styleData.textLabel,
            font: "12px Calibri,sans-serif",
            fill: new Fill({ color: Colors.white }),
            stroke: new Stroke({ color: Colors.black, width: 3 })
        });
    }
    let image = undefined;
    if (styleData.image) {
        image = new Icon({
            src: styleData.image
        });
    }
    return new Style({
        fill: new Fill({
            color: styleData.fillColor
        }),
        stroke: new Stroke({
            color: styleData.strokeColor,
            width: 2,
            lineDash: styleData.strokeLineDash
        }),
        text: text,
        image: image
    });
}

export function GetLaancAirspaceStyle(feature, airspaceLayers) {
    let strokeColor = "rgba(0, 0, 0, 0)";
    let fillColor = "rgba(0, 0, 0, 0)";
    let lineDash = undefined;

    const properties = feature.getProperties();
    const setting = airspaceLayers.find((layer) => {
        return layer.local_type === properties.LOCAL_TYPE;
    });
    if (setting && setting.visible) {
        switch (properties.LOCAL_TYPE) {
            case "CLASS_B":
                strokeColor = Colors.blue1;
                fillColor = LowOpacityColors.blue1;
                lineDash = undefined;
                break;
            case "CLASS_C":
                strokeColor = Colors.pink1;
                fillColor = LowOpacityColors.pink1;
                lineDash = undefined;
                break;
            case "CLASS_D":
                strokeColor = Colors.blue2;
                fillColor = LowOpacityColors.blue2;
                lineDash = [8, 4];
                break;
            case "MODE C":
                strokeColor = Colors.pink1;
                fillColor = LowOpacityColors.pink1;
                lineDash = undefined;
                break;
            default:
                if (properties.LOCAL_TYPE.includes("CLASS_E")) {
                    strokeColor = Colors.pink2;
                    fillColor = LowOpacityColors.pink2;
                    lineDash = [8, 4];
                }
        }
    }
    return new Style({
        fill: new Fill({
            color: fillColor
        }),
        stroke: new Stroke({
            color: strokeColor,
            width: 2,
            lineDash: lineDash
        })
    });
}

export function getSpecialUseAirspaceStyle(feature, layers) {
    let style = new Style({
        fill: undefined,
        stroke: undefined
    });
    const properties = feature.getProperties();
    if (properties && properties.TYPE_CODE) {
        const layer = layers.find((layer) => {
            return layer.type_code === properties.TYPE_CODE;
        });
        if (layer && layer.visible) {
            const suAirspaceStyle = LAANC_STYLE_DATA["SU_AIRSPACE"][properties.TYPE_CODE];
            if (suAirspaceStyle) {
                style = new Style({
                    fill: new Fill({
                        color: suAirspaceStyle.fillColor
                    }),
                    stroke: new Stroke({
                        color: suAirspaceStyle.strokeColor,
                        width: 2,
                        lineDash: suAirspaceStyle.strokeLineDash
                    })
                });
            }
        }
    }
    return style;
}

export const Rotations = {
    RIGHT_MIDDLE: {
        offsetX: -20,
        offsetY: 0,
        textAlign: "right",
        textBaseline: "middle"
    },
    RIGHT_BOTTOM: {
        offsetX: -15,
        offsetY: -15,
        textAlign: "right",
        textBaseline: "bottom"
    },
    CENTER_BOTTOM: {
        offsetX: 0,
        offsetY: -20,
        textAlign: "center",
        textBaseline: "bottom"
    },
    LEFT_BOTTOM: {
        offsetX: 15,
        offsetY: -15,
        textAlign: "left",
        textBaseline: "bottom"
    },
    LEFT_MIDDLE: {
        offsetX: 20,
        offsetY: 0,
        textAlign: "left",
        textBaseline: "middle"
    },
    LEFT_TOP: {
        offsetX: 15,
        offsetY: 15,
        textAlign: "left",
        textBaseline: "top"
    },
    CENTER_TOP: {
        offsetX: 0,
        offsetY: 20,
        textAlign: "center",
        textBaseline: "top"
    },
    RIGHT_TOP: {
        offsetX: -15,
        offsetY: 15,
        textAlign: "right",
        textBaseline: "top"
    }
};

export function getLabelRotation(feature, settings) {
    const courseDeg = feature.get("asd").course_deg;
    const rotationOffset = feature.get("label_rotation") || 0;

    // feature should be rotated with respect to the heading, setting, and user preference
    const rotation = (courseDeg + rotationOffset + settings.track_label_rotation_deg) % 360;
    if ((rotation > 337.5 && rotation <= 360) || (rotation >= 0 && rotation < 22.5)) {
        return Rotations.CENTER_BOTTOM;
    }
    if (rotation >= 22.5 && rotation <= 60) {
        return Rotations.LEFT_BOTTOM;
    }
    if (rotation >= 61 && rotation <= 105) {
        return Rotations.LEFT_MIDDLE;
    }
    if (rotation >= 106 && rotation <= 150) {
        return Rotations.LEFT_TOP;
    }
    if (rotation >= 151 && rotation <= 195) {
        return Rotations.CENTER_TOP;
    }
    if (rotation >= 196 && rotation <= 240) {
        return Rotations.RIGHT_TOP;
    }
    if (rotation >= 241 && rotation <= 285) {
        return Rotations.RIGHT_MIDDLE;
    }
    return Rotations.RIGHT_BOTTOM;
}

// Takes the current data tag rotation & rotates it 45 degrees clockwise.
export function setNextLabelRotation(feature, setLabelRotations, settings) {
    if (!feature || !feature.getStyle() || !Array.isArray(feature.getStyle()) || !settings) {
        return;
    }

    const oldRotationOffset = feature.get("label_rotation") || 0;
    const newRotationOffset = (oldRotationOffset + 45) % 360;
    feature.set("label_rotation", newRotationOffset);
    setLabelRotations((prev) => ({ ...prev, [feature.getId()]: { rotation: newRotationOffset, timestamp: feature.get("last_asd_update_ms") } }));

    const style = feature.getStyle().find((s) => s.asd_track_label);
    const text = style.getText();
    const rotation = getLabelRotation(feature, settings);
    text.setOffsetX(rotation.offsetX);
    text.setOffsetY(rotation.offsetY);
    text.setTextAlign(rotation.textAlign);
    text.setTextBaseline(rotation.textBaseline);
    style.setText(text);
}

export const getLabelFillAndStrokeFromSettings = (settings) => {
    let fill = undefined;
    let stroke = undefined;

    if (settings.track_label_color === "YELLOW") {
        fill = new Fill({ color: Colors.yellow });
        stroke = new Stroke({ color: Colors.black, width: 3 });
    } else if (settings.track_label_color === "GREEN") {
        fill = new Fill({ color: Colors.green });
        stroke = new Stroke({ color: Colors.black, width: 3 });
    } else {
        fill = new Fill({ color: Colors.white });
        stroke = new Stroke({ color: Colors.black, width: 3 });
    }
    return {
        fill: fill,
        stroke: stroke
    };
};

export function getIdentifierTextFromTrackAndSettings(track, settings, user) {
    if (!track) return "";

    let identifer = "";
    if (settings.track_label_identifier === "SQUAWK_CODE") {
        identifer = track.squawk;
    } else if (settings.track_label_identifier === "CALLSIGN" && track.tagged_callsign) {
        identifer = track.tagged_callsign[user.organization_id];
    } else if (settings.track_label_identifier === "TRACK_LABEL") {
        identifer = track.label;
    }
    if (identifer && identifer.length > 0) {
        return `${identifer}\n`;
    } else {
        return "";
    }
}

export function getLabelTextFromFeatureAndUser(feature, user, settings) {
    const properties = feature.getProperties();
    const identiferText = getIdentifierTextFromTrackAndSettings(properties.asd, settings, user);
    const reinforced = properties.asd.reinforced_flag ? true : false;

    let altitudeText = "";
    if (isNaN(properties.asd.alt_hae_ft) === false) {
        const speedIndicator = GetVerticalSpeedIndicator(properties.asd.climb_rate_fpm);
        altitudeText = `${properties.asd.alt_hae_ft}ft${speedIndicator ? ` ${speedIndicator}` : ""}${reinforced ? " - R" : ""}\n`;
    } else {
        altitudeText = `NAR${reinforced ? " - R" : ""}\n`;
    }

    const groundSpeedText = `${properties.asd.ground_speed_kn}k`;
    const text = `${identiferText}${altitudeText}${groundSpeedText}`;

    const { offsetX, offsetY, textAlign, textBaseline } = getLabelRotation(feature, settings);
    const { fill, stroke } = getLabelFillAndStrokeFromSettings(settings);

    return new Text({
        text: text,
        font: "14px Arial,sans-serif",
        fill: fill,
        stroke: stroke,
        textAlign: textAlign,
        offsetX: offsetX,
        offsetY: offsetY,
        textBaseline: textBaseline
    });
}

export function getAsdPredLineStyle(properties, sourceTypeSettings) {
    let vehicleColor = sourceTypeSettings.color;
    if (properties.asd.simulated) {
        vehicleColor = Colors.lightBlue2;
    }
    return new Style({
        stroke: new Stroke({
            color: vehicleColor,
            width: 2
        })
    });
}

export function getAsdHistLineStyle(properties, sourceTypeSettings) {
    let vehicleColor = sourceTypeSettings.color;
    if (properties.asd.simulated) {
        vehicleColor = Colors.lightBlue2;
    }
    return new Style({
        stroke: new Stroke({
            color: vehicleColor,
            width: 2,
            lineDash: [5, 5]
        })
    });
}

export function getAsdDpzStyle(settings) {
    if (settings.well_clear_volume) {
        return new Style({
            stroke: new Stroke({
                color: Colors.green,
                width: 2
            })
        });
    } else {
        return new Style({
            stroke: undefined
        });
    }
}
export function getAsdOuterDpzStyle(settings) {
    if (settings.second_well_clear_volume_visibility) {
        return new Style({
            stroke: new Stroke({ color: Colors.green, width: 2 })
        });
    } else {
        return new Style({ stroke: undefined });
    }
}

export function getAsdTrackIconStyle(properties, settings, sourceTypeSettings) {
    const emitterType = properties.asd && properties.asd.adsb ? properties.asd.adsb.emitter_cat : 0;
    let scale = settings.entity_size + 0.2;

    let src = `../static/2d-models/shapes/${sourceTypeSettings.icon}.png`;
    if (sourceTypeSettings.icon === "icon") {
        src = allModels.get(0);
        if (properties.asd.source_type === "TELEMETRY") {
            src = allModels.get(8);
        } else if (emitterType && allModels.has(emitterType)) {
            src = allModels.get(emitterType);
        }
    } else {
        scale * 0.75;
    }
    let vehicleColor = sourceTypeSettings.color;
    if (properties.asd.simulated) {
        vehicleColor = Colors.lightBlue2;
    }
    return new Style({
        image: new Icon({
            src: src,
            color: vehicleColor,
            scale: scale,
            rotation: -(properties.asd.rotation * (Math.PI / 180))
        })
    });
}

export function getAsdTrackLabelStyle(feature, layers, settings, user) {
    const enabledLabels = settings.label_visible === "on";
    const visibleLabel = feature.get("show_label");
    const enabledAlertVolumeLabels = settings.label_visible === "alerts" && feature.get("alert_volume_confliction_alert") && layers.alert_volumes;

    if (enabledLabels || visibleLabel || enabledAlertVolumeLabels) {
        const text = getLabelTextFromFeatureAndUser(feature, user, settings);
        const style = new Style({ text: text });
        style.asd_track_label = true;
        return style;
    } else {
        return new Style({
            text: undefined
        });
    }
}

export function getAsdZeroConflictionStyle(feature, settings) {
    const activeZca = feature.get("zero_confliction_alerts");
    if (!Array.isArray(activeZca)) {
        return null;
    }

    const ownshipAlerts = [];
    const intruderAlerts = [];

    const asd = feature.get("asd");
    activeZca.forEach((alert) => {
        if (alert.ownship_track.id === asd.id) {
            ownshipAlerts.push(alert);
        }
        if (alert.intruder_track.id === asd.id) {
            intruderAlerts.push(alert);
        }
    });

    let image = undefined;
    if (ownshipAlerts.length) {
        let zcaColor = "255, 235, 0"; // yellow
        if (ownshipAlerts.find(({ severity }) => severity === "WARNING")) {
            zcaColor = "200, 0, 0"; // red
        }
        image = getCircleImageFromColor(zcaColor);
    }

    let text = undefined;
    if (intruderAlerts.length) {
        if (settings && settings.zca_data_block_visibility === true) {
            text = getZeroConflictAlertTextFromAlert(intruderAlerts[0]);
        }
        let zcaColor = "255, 235, 0"; // yellow
        if (intruderAlerts.find(({ severity }) => severity === "WARNING")) {
            zcaColor = "200, 0, 0"; // red
        }
        image = getCircleImageFromColor(zcaColor);
    }

    if (!image && !text) {
        return null;
    }

    return new Style({ text: text, image: image });
}

export function getAsdWellClearVolumeStyle(feature, settings, user, isTagged = false) {
    const asd = feature.get("asd");
    if (!asd) return null;

    const zcaEnabled = asd.show_dpz[user.organization_id];
    const wcvEnabled = settings.well_clear_volume;
    if ((zcaEnabled || isTagged) && wcvEnabled) {
        const greenWcv = getCircleImageFromColor("0, 200, 0");
        return new Style({ text: undefined, image: greenWcv });
    } else {
        return null;
    }
}

export function getAsdEotOrCoastedStyle(properties) {
    if (properties.asd.eot) {
        return new Style({
            image: new Icon({
                src:
                    "data:image/svg+xml;base64," +
                    btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <line x1="4" y1="4" x2="20" y2="20" stroke="red" stroke-width="2"/>
                    <line x1="20" y1="4" x2="4" y2="20" stroke="red" stroke-width="2"/>
                    </svg>
                    `)
            })
        });
    }

    if (properties.asd.coasted) {
        return new Style({
            image: new Icon({
                src:
                    "data:image/svg+xml;base64," +
                    btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <line x1="4" y1="4" x2="20" y2="20" stroke="yellow" stroke-width="2"/>
              <line x1="20" y1="4" x2="4" y2="20" stroke="yellow" stroke-width="2"/>
              </svg>
              `)
            })
        });
    }

    return new Style({ image: undefined });
}

export function getAsdFeatureStyleFromLayersAndSettings(feature, layers, settings, user, taggedVehicles = []) {
    if (!feature.get("asd")) return [];

    const properties = feature.getProperties();
    const asd = properties.asd;

    // Check: Altitude Range
    const altHaeFt = asd.alt_hae_ft;
    if (isNaN(altHaeFt)) {
        if (layers && layers.nar_tracks === false && asd.source_type !== "TELEMETRY") {
            return [];
        }
    } else if (layers && layers.altitude_range) {
        const altitude = parseFloat(asd.alt_hae_ft) < 0 ? 1 : asd.alt_hae_ft;
        const altitudeWithinRange = altitude >= layers.altitude_range[0] && altitude <= layers.altitude_range[1];
        if (!altitudeWithinRange && asd.source_type !== "TELEMETRY") {
            return [];
        }
    }

    // Check: Surveillance Sources Map Layer
    if (layers && !layers.surveillance_sources) {
        return [];
    }

    // Check: Source Setting Visibility
    if (settings.source_mapping[asd.source_id] && !settings.source_mapping[asd.source_id].visibility) {
        return [];
    }

    // Check: Source Type (icon/color) Presence
    const sourceSettings = settings.source_type_settings.find((source) => source.source_type === asd.source_type);
    if (!sourceSettings) return [];

    const featureID = feature.getId();
    const isFeatureIcon = !featureID.includes("_");
    const isFeaturePredLine = featureID.includes("_pl");
    const isFeatureHistLine = featureID.includes("_hl");
    const isFeatureDpz = featureID.includes("_dpz");
    const isFeatureZcLine = featureID.includes("_") && !featureID.includes("_pl") && !featureID.includes("_hl") && !featureID.includes("_dpz");

    const taggedVehicle = taggedVehicles.find((v) => featureID.includes(v.entity_uuid));
    const isFeatureTagged = taggedVehicle && (user.user_role === "Admin" || user.organization_id === taggedVehicle.organization_id);

    if (isFeaturePredLine && settings.pred_line) {
        return [getAsdPredLineStyle(properties, sourceSettings)];
    } else if (isFeatureHistLine && settings.hist_line) {
        return [getAsdHistLineStyle(properties, sourceSettings)];
    } else if (isFeatureDpz && (asd.show_dpz[user.organization_id] || isFeatureTagged)) {
        const isOuterDpz = featureID.includes("_outer");
        if (isOuterDpz) {
            return [getAsdOuterDpzStyle(settings)];
        } else {
            return [getAsdDpzStyle(settings)];
        }
    } else if (isFeatureZcLine) {
        const zcLineStyle = feature.getStyle();
        if (zcLineStyle) {
            return zcLineStyle;
        } else {
            return [];
        }
    } else if (!isFeatureIcon) {
        return [];
    }

    // All the different styles for regular ASD entities.
    const asdIconStyle = getAsdTrackIconStyle(properties, settings, sourceSettings);
    const asdLabelStyle = getAsdTrackLabelStyle(feature, layers, settings, user);
    const asdCircleStyles = getAsdCircleStyles(feature, layers, settings, user, isFeatureTagged);
    const asdEotOrCoastedStyle = getAsdEotOrCoastedStyle(properties);
    return [asdIconStyle, asdLabelStyle, ...asdCircleStyles, asdEotOrCoastedStyle];
}

export function getAsdCircleStyles(feature, layers, settings, user, isTagged = false) {
    // Top Priority -- ZC Styles.
    const zcaStyle = getAsdZeroConflictionStyle(feature, settings);
    if (zcaStyle) return [zcaStyle];

    // Next Priority -- alert confliction styles.
    const alertConflictionStyle = getAlertVolumeConflictionAlertStyle(feature, layers);
    if (alertConflictionStyle) return [alertConflictionStyle];

    // Lowest priority -- well clear volume styles.
    const wellClearStyle = getAsdWellClearVolumeStyle(feature, settings, user, isTagged);
    return wellClearStyle ? [wellClearStyle] : [new Style()];
}

export function getAlertVolumeConflictionAlertStyle(feature, layers) {
    if (feature.get("alert_volume_confliction_alert") && layers.alert_volumes) {
        return new Style({
            image: new CircleStyle({
                radius: 20,
                fill: undefined,
                stroke: new Stroke({
                    color: Colors.yellow,
                    width: 2
                })
            })
        });
    } else {
        return null;
    }
}

export function getCircleImageFromColor(colorRGB) {
    return new CircleStyle({
        radius: 20,
        fill:
            colorRGB === "0, 200, 0"
                ? new Fill({
                      color: `rgba(${colorRGB}, .1)`
                  })
                : undefined,
        stroke: new Stroke({
            color: `rgba(${colorRGB}, .8)`,
            width: 2
        })
    });
}

export function getZeroConflictAlertTextFromAlert(zcAlert) {
    const horizSepFt = ConvertMetersToFeet(zcAlert.horiz_sep_m);
    const vertSepFt = ConvertMetersToFeet(zcAlert.vert_sep_m);
    const severity = zcAlert.severity;
    const timeLos = zcAlert.time_los_sec;
    const label = `Horz: ${horizSepFt}ft\nVert: ${vertSepFt}ft\nAlert: ${severity}\nTime: ${timeLos}s`;

    let backgroundColor = "255, 235, 0";
    if (severity === "WARNING") {
        backgroundColor = "200, 0, 0";
    }
    return new Text({
        text: label,
        font: "16px Calibri, sans-serif",
        fill: new Fill({
            color: "#fff"
        }),
        stroke: new Stroke({
            color: "#000",
            width: 2
        }),
        backgroundFill: new Fill({
            color: `rgba(${backgroundColor}, 1)`
        }),
        padding: [5, 5, 5, 5],
        offsetY: -75
    });
}

export function getColorFromLaancType(state, opacity) {
    if (state === "AUTO_APPROVED") {
        return `rgba(224, 210, 104, ${opacity})`;
    } else if (state === "FURTHER_COORDINATION" || state === "BLOCKED") {
        return `rgba(203, 76, 78, ${opacity})`;
    } else {
        return `rgba(255, 255, 255, ${opacity})`;
    }
}

const GeoJson = new GeoJSON();
const Kml = new KML();

// Searches for a layer given its name.
export function GetLayer(map, layerName) {
    if (!map) return null;

    for (const layer of map.getLayers().getArray()) {
        if (layer.get("name") === layerName) {
            return layer;
        }
    }
    return null;
}

// Searches for a source given a layer's name.
export function GetSource(map, layerName) {
    const layer = GetLayer(map, layerName);
    return layer ? layer.getSource() : null;
}

// Searches for a list of features given a layer's name.
export function GetFeatures(map, layerName) {
    const source = GetSource(map, layerName);
    return source ? source.getFeatures() : null;
}

// Searches for a feature given a layer's name and feature's UUID.
export function GetFeature(map, layerName, featureUUID) {
    const source = GetSource(map, layerName);
    return source && featureUUID ? source.getFeatureById(featureUUID) : null;
}

export function GetAllFeaturesWithId(map, layerName, featureUUID) {
    const featuresWithID = [];

    const source = GetSource(map, layerName);
    if (source) {
        const features = source.getFeatures() || [];
        features.forEach((feature) => {
            if (feature.getId().includes(featureUUID)) {
                featuresWithID.push(feature);
            }
        });
    }
    return featuresWithID;
}

// Jumps to the specified feature on the map.
export function JumpToFeature(map, feature) {
    if (!map || !feature) return;

    if (feature.get("asd")) {
        map.getView().fit(feature.getGeometry(), { maxZoom: 12 });
    } else if (feature.getGeometry() instanceof GeometryCollection) {
        for (const geo of feature.getGeometry().getGeometries()) {
            if (geo instanceof Polygon || geo instanceof LineString) {
                map.getView().fit(geo, { padding: [200, 200, 200, 200] });
                break;
            }
        }
    } else {
        map.getView().fit(feature.getGeometry(), { padding: [200, 200, 200, 200] });
    }
}

export function JumpToFeatures(map, features, pad = 100) {
    if (features.length === 0) return;

    const extent = createEmpty();
    features.forEach((feature) => {
        extend(extent, feature.getGeometry().getExtent());
    });
    map.getView().fit(extent, {
        maxZoom: 16,
        padding: [pad, pad, pad, pad]
    });
}

// Converts a feature into the GeoJSON format.
export function ConvertGeometryToGeoJson(geometry) {
    if (typeof geometry.getRadius === "function") {
        const polygon = circle(geometry.getCenter(), geometry.getRadius(), { units: "degrees", steps: 128 });
        return polygon.geometry;
    } else {
        return GeoJson.writeGeometryObject(geometry);
    }
}

export function GetCoordinatesForSubmissions(geometry) {
    if (typeof geometry.getRadius === "function") {
        return { coordinates: [[geometry.getCenter()]] };
    } else if (geometry.get("type") === "Circle") {
        return { coordinates: [[geometry.getInteriorPoint().getCoordinates()]] };
    } else if (geometry instanceof Polygon) {
        return GeoJson.writeGeometryObject(geometry);
    } else if (geometry instanceof LineString) {
        return { type: "LineString", coordinates: [GeoJson.writeGeometryObject(geometry).coordinates] };
    } else if (geometry instanceof Point) {
        return { type: "Point", coordinates: [[GeoJson.writeGeometryObject(geometry).coordinates]] };
    } else if (!(geometry instanceof GeometryCollection)) {
        return null;
    }

    const geometries = geometry
        .getGeometries()
        .map((geo) => {
            if (geo instanceof Polygon) {
                return GeoJson.writeGeometryObject(geo);
            } else if (geo instanceof LineString) {
                return { type: "LineString", coordinates: [GeoJson.writeGeometryObject(geometry).coordinates] };
            }
            return null;
        })
        .filter(Boolean); // Remove nulls or undefined from the array

    return geometries.length === 1 ? geometries[0] : geometries;
}

// Converts our CMP volumes structure to an array of OL features.
export function ConvertCmpVolumesToFeatures(cmpVolumes, userMapSettings) {
    const features = [];
    for (const cmpVolume of cmpVolumes) {
        const volumes = "volumes" in cmpVolume ? cmpVolume.volumes : cmpVolume.polygons;
        for (let i = 0; i < volumes.length; ++i) {
            const volume = volumes[i];

            let geometry;
            if (volume.polygon) {
                const coordinates = [volume.polygon.map(({ lng, lat }) => [lng, lat])];
                geometry = new Polygon(coordinates);
            } else if (volume.type === "polygon") {
                const coordinates = [volume.vertices.map(({ lng, lat }) => [lng, lat])];
                geometry = new Polygon(coordinates);
            } else if (volume.circle) {
                const center = [volume.circle.center.lng, volume.circle.center.lat];
                const radius = volume.circle.radius_m;
                geometry = circular(center, radius, 128);
                geometry.set("type", "Circle");
            } else if (volume.type === "circle") {
                const center = [volume.lng, volume.lat];
                const radius = volume.radius;
                geometry = circular(center, radius, 128);
                geometry.set("type", "Circle");
            }
            if (!geometry) {
                continue;
            }
            const feature = new Feature({
                geometry: geometry
            });
            feature.set("name", cmpVolume.name);

            // Note: Fine for now, but if we need to add any other volume types,
            //       it would be better to just pass in the type in this function.
            if ("flight_uuid" in cmpVolume && "uuid" in cmpVolume === false) {
                feature.setId(`${cmpVolume.flight_uuid}_${i}`);
                feature.set("flight", cmpVolume);
            } else if ("constraint_uuid" in cmpVolume) {
                feature.setId(`${cmpVolume.constraint_uuid}_${i}`);
                feature.set("constraint", cmpVolume);
            } else if ("uuid" in cmpVolume) {
                feature.setId(`${cmpVolume.uuid}_${i}`);
                feature.set("alert_volume", cmpVolume);
            } else if ("id" in cmpVolume) {
                feature.setId(`${cmpVolume.id}_${i}`);
                feature.set("coa_volume", cmpVolume);
            }
            const style = getGeometryStyleFromFeatureAndSettings(feature, userMapSettings);
            feature.setStyle(style);

            features.push(feature);
        }
    }
    return features;
}

export function ConvertLAANCVolumesToFeatures(laancVolumes) {
    const features = [];

    laancVolumes.forEach((volume, index) => {
        let geometry;
        if (volume.polygon) {
            const coordinates = [volume.polygon.map(({ lng, lat }) => [lng, lat])];
            geometry = new Polygon(coordinates);
        } else if (volume.circle) {
            const center = [volume.circle.center.lng, volume.circle.center.lat];
            const radius = volume.circle.radius_m;
            geometry = circular(center, radius, 128);
            geometry.set("type", "Circle");
        }
        if (!geometry) {
            return;
        }
        const feature = new Feature({
            geometry: geometry
        });
        feature.set("name", "Volume " + (index + 1));
        feature.setId("Volume " + (index + 1));
        feature.set("laanc_volume", volume);

        const style = new Style({
            fill: new Fill({
                color: getColorFromLaancType(volume.faa_request_type, 0.5)
            }),
            stroke: new Stroke({
                color: getColorFromLaancType(volume.faa_request_type, 1),
                width: 2
            })
        });
        feature.setStyle(style);
        features.push(feature);

        index += 1;
    });

    return features;
}

export function ConvertDfrToFeatures(dfr, telemetry, userMapSettings) {
    // Flight Volume
    if (!dfr.telemetry) {
        return ConvertCmpVolumesToFeatures([dfr], userMapSettings);
    }

    // Showing full telemetry that was recorded.
    const getTelemetryFeature = (dfr, telemetry) => {
        const coords = telemetry.map((p) => [p.location.x, p.location.y]);
        const feature = new Feature({ geometry: new LineString(coords), coords, dfr });
        feature.setStyle((feature) => {
            const coords = feature.get("coords");
            return [
                new Style({ stroke: new Stroke({ color: "#4298CA", width: 4 }) }),
                new Style({ geometry: new Point(coords[0]), image: new CircleStyle({ radius: 4, fill: new Fill({ color: "#1b5e20" }) }) }),
                new Style({ geometry: new Point(coords[coords.length - 1]), image: new CircleStyle({ radius: 4, fill: new Fill({ color: "#b71c1c" }) }) })
            ];
        });
        return feature;
    };

    const groups = {};
    for (const tracked of telemetry) {
        const key = tracked.track_uuid;
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(tracked);
    }

    const features = [];
    for (const group of Object.values(groups)) {
        features.push(getTelemetryFeature(dfr, group));
    }

    return features;
}

// Converts a KML file into an OL feature.
// It will include "name" and "altitudes" properties.
/* istanbul ignore next */
export async function ConvertKmlFileToFeatures(file) {
    return new Promise((resolve, reject) => {
        if (!/(\.kml|\.kmz)$/i.exec(file.name.toLowerCase())) {
            return reject("Please choose a valid KML/KMZ file type.");
        } else if (file.size > 500000) {
            return reject("Please choose a KML file that is less than 500KB.");
        }

        const fileReader = new FileReader();
        fileReader.onloadend = (e) => {
            const kmlDoc = new DOMParser().parseFromString(e.target.result, "text/xml");
            const kmlFeatures = Kml.readFeatures(kmlDoc);
            const validFeatures = [];
            for (const kmlFeature of kmlFeatures) {
                const geometry = kmlFeature.getGeometry();
                if (geometry && (geometry instanceof Polygon || geometry instanceof LineString || geometry instanceof GeometryCollection)) {
                    validFeatures.push(kmlFeature);
                }
            }

            if (validFeatures.length === 0) {
                return reject("Please choose a KML file with at least 1 valid polygon.");
            }
            if (!("name" in validFeatures[0].getProperties)) {
                validFeatures[0].set("name", "KML File");
            }

            validFeatures[0].set("altitudes", ExtractAltitudesFromFeatures(validFeatures));
            resolve(validFeatures);
        };
        fileReader.onerror = () => reject("KML file could not be parsed correctly.");
        fileReader.readAsText(file);
    });
}

// Extracts the minimum and maximum altitudes out of an OL geometry.
/* istanbul ignore next */
export function ExtractAltitudesFromFeatures(features) {
    const altitudes = {
        minAltitude: 0,
        maxAltitude: 0
    };

    const extractAltitudes = (geometry) => {
        if (!geometry || (!(geometry instanceof Polygon) && !(geometry instanceof LineString))) {
            return;
        }
        const coordinates = geometry instanceof Polygon ? geometry.getCoordinates()[0] : geometry.getCoordinates();
        for (const coordinate of coordinates) {
            altitudes.minAltitude = Math.min(altitudes.minAltitude, coordinate[2]);
            altitudes.maxAltitude = Math.max(altitudes.maxAltitude, coordinate[2]);
        }
    };
    features.forEach((feature) => {
        const geometry = feature.getGeometry();
        if (geometry instanceof GeometryCollection) {
            geometry.getGeometries().forEach((geo) => extractAltitudes(geo));
        } else {
            extractAltitudes(geometry);
        }
    });
    return altitudes;
}

// Checks if the map is currently being drawn/modified/snapped on.
export function IsMapBeingDrawnOn(map) {
    for (const interaction of map.getInteractions().getArray()) {
        if (interaction instanceof Draw || interaction instanceof Modify || interaction instanceof Translate || interaction instanceof Snap) {
            return true;
        }
    }
    return false;
}

export function getGeofenceFeatureFromGeometryAndBuffer(geometry, buffer, settings) {
    const coords = geometry.getCoordinates();
    const linestring = lineString(coords);
    const buffered = turfBuffer(linestring, buffer, { units: "feet" });

    const geofencePolygon = new Polygon(buffered.geometry.coordinates);
    const geofenceFeature = new Feature({ geometry: geofencePolygon });
    geofenceFeature.setStyle(GetGeofenceStyle(settings));

    return geofenceFeature;
}

export function convertHexColorToRgbaColor(hex, opacity) {
    hex = hex.replace("#", "");

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function getGeometryStyleFromColor(colorHex) {
    return new Style({
        stroke: new Stroke({
            color: convertHexColorToRgbaColor(colorHex, 1),
            width: 2
        }),
        fill: new Fill({
            color: convertHexColorToRgbaColor(colorHex, 0.2)
        })
    });
}

export function getGeometryStyleFromFeatureAndSettings(feature, userMapSettings) {
    const properties = feature.getProperties();

    let colorHex = "#000";
    let lineDash = undefined;

    if ("flight" in properties) {
        colorHex = GetColorFromState(properties.flight.state, userMapSettings.op_state_settings);
    } else if ("constraint" in properties) {
        colorHex = GetColorFromState(properties.constraint.state, userMapSettings.op_state_settings);
    } else if ("alert_volume" in properties) {
        colorHex = properties.alert_volume.color_rgb;
    } else if ("coa_volume" in properties) {
        colorHex = "#cd6c17";
        lineDash = [5, 5];
    }
    let text = undefined;
    if (userMapSettings.volume_labels) {
        text = new Text({
            text: feature.get("name"),
            font: "12px Calibri,sans-serif",
            fill: new Fill({ color: "#fff" }),
            stroke: new Stroke({ color: "#000", width: 2 }),
            overflow: true
        });
    }
    return new Style({
        stroke: new Stroke({
            color: convertHexColorToRgbaColor(colorHex, 1),
            width: 2,
            lineDash: lineDash
        }),
        fill: new Fill({
            color: convertHexColorToRgbaColor(colorHex, userMapSettings.op_opacity)
        }),
        text: text
    });
}

export function wrapNeg180To180(num) {
    return ((((num + 180) % 360) + 360) % 360) - 180;
}

export function extrapolatePosition(lat, lon, hdgDeg, distanceMeters) {
    const M_PER_NM = 1852.0;
    const RAD_PER_DEG = Math.PI / 180.0;
    const DEG_PER_RAD = 180.0 / Math.PI;

    hdgDeg = hdgDeg % 360;
    let distanceDeg = distanceMeters / M_PER_NM / 60.0;

    let b = distanceDeg * RAD_PER_DEG;
    let cosB = Math.cos(b);
    let sinB = Math.sin(b);
    let a = (90.0 - lat) * RAD_PER_DEG;
    let cosA = Math.cos(a);
    let sinA = Math.sin(a);
    let C = hdgDeg * RAD_PER_DEG;
    let cosC = Math.cos(C);

    let cosC2 = cosA * cosB + sinA * sinB * cosC;
    let c = Math.acos(Math.min(Math.max(cosC2, -1.0), 1.0));
    let sinC = Math.sin(c);
    const newLat = 90.0 - c * DEG_PER_RAD;

    let cosB2 = (cosB - cosC2 * cosA) / (sinC * sinA);
    let B = Math.acos(Math.min(Math.max(cosB2, -1.0), 1.0));
    if (C > Math.PI) {
        B = -B;
    }
    const newLon = wrapNeg180To180(lon + B * DEG_PER_RAD);

    return [newLat, newLon];
}

export function getPredLineCoordinatesFromTrackAndSettings(track, settings) {
    const bearing = track.course_deg;
    const distance = track.ground_speed_mps * settings.pred_line_lead_time;

    const startLon = track.lon_deg;
    const startLat = track.lat_deg;

    const [finalLat, finalLon] = extrapolatePosition(startLat, startLon, bearing, distance);

    return [
        [track.lon_deg, track.lat_deg],
        [finalLon, finalLat]
    ];
}

export function getPredLineStyleFromTrackAndSettings(track, settings) {
    const sourceType = settings.source_type_settings.find((setting) => setting.source_type === track.source_type);
    if (sourceType && settings.pred_line) {
        return new Style({
            stroke: new Stroke({
                color: sourceType.color,
                width: 2
            })
        });
    } else {
        return new Style({
            stroke: null
        });
    }
}

export function getHistLineCoordinatesFromTrack(track) {
    const historyLineCoordinates = [];
    for (let i = 0; i < track.history_locations.length; i += 3) {
        const lon = track.history_locations[i];
        const lat = track.history_locations[i + 1];
        historyLineCoordinates.push([lon, lat]);
    }
    return historyLineCoordinates;
}

export function getHistLineStyleFromTrackAndSettings(track, settings) {
    const sourceType = settings.source_type_settings.find((setting) => setting.source_type === track.source_type);
    if (sourceType && settings.hist_line) {
        return new Style({
            stroke: new Stroke({
                color: sourceType.color,
                width: 2,
                lineDash: [5, 5]
            })
        });
    } else {
        return new Style({
            stroke: null
        });
    }
}

export function getDpzCoordinatesFromTrack(track, settings) {
    const oneNauticalMileMeters = 1852;
    const threeNauticalMilesMeters = oneNauticalMileMeters * settings.well_clear_volume_radius_nm;

    const center = [track.lon_deg, track.lat_deg];
    const geometry = circular(center, threeNauticalMilesMeters, 128);

    return geometry.getCoordinates();
}
export function getOuterDpzCoordinatesFromTrack(track, settings) {
    const oneNauticalMileMeters = 1852;
    const outerRadiusNauticalMiles = oneNauticalMileMeters * settings.second_well_clear_volume_radius_nm;

    const center = [track.lon_deg, track.lat_deg];
    const geometry = circular(center, outerRadiusNauticalMiles, 128);

    return geometry.getCoordinates();
}
export function getTagButtonVisibility(user, feature) {
    if ("asd" in feature.getProperties()) {
        return user.user_role === "Admin" || user.user_role === "Airspace Manager" || user.user_role === "Operator";
    } else {
        return false;
    }
}
export function getPublishConstraintButtonVisibility(user, feature) {
    const properties = feature.getProperties();
    if ("constraint" in properties && properties.constraint.owner === "cmp-ui-online") {
        return properties.constraint.state !== "ACCEPTED" && (user.user_role === "Admin" || user.user_role === "Airspace Manager");
    } else {
        return false;
    }
}
export function getEditConstraintButtonVisibility(user, feature) {
    const properties = feature.getProperties();
    if ("constraint" in properties && properties.constraint.owner === "cmp-ui-online") {
        return user.user_role === "Admin" || user.user_role === "Airspace Manager";
    } else {
        return false;
    }
}
export function getDeleteConstraintButtonVisibility(user, feature) {
    const properties = feature.getProperties();
    if ("constraint" in properties && properties.constraint.owner === "cmp-ui-online") {
        if (user.user_role === "First Responder") {
            return properties.constraint.created_user_id == user.id;
        } else {
            return user.user_role === "Admin" || user.user_role === "Airspace Manager";
        }
    } else {
        return false;
    }
}

export function updateFeaturesZeroConflictionAlerts(feature, zcAlert) {
    const zcAlerts = feature.get("zero_confliction_alerts") || [];

    if (zcAlert.state === "ACTIVE") {
        if (zcAlerts.length > 0) {
            const zcExists = zcAlerts.find((alert) => alert.alert_uuid === zcAlert.alert_uuid);
            if (zcExists) {
                const updatedZcAlerts = zcAlerts.map((alert) => {
                    if (alert.alert_uuid === zcAlert.alert_uuid) {
                        return zcAlert;
                    } else {
                        return alert;
                    }
                });
                feature.set("zero_confliction_alerts", updatedZcAlerts);
            } else {
                feature.set("zero_confliction_alerts", [...zcAlerts, zcAlert]);
            }
        } else {
            feature.set("zero_confliction_alerts", [zcAlert]);
        }
    } else if (zcAlert.state === "EXPIRED") {
        const updatedZcAlerts = zcAlerts.filter((alert) => alert.alert_uuid !== zcAlert.alert_uuid);
        if (updatedZcAlerts.length === 0) {
            feature.unset("zero_confliction_alerts");
        } else {
            feature.set("zero_confliction_alerts", updatedZcAlerts);
        }
    }
}

// create a feature for zca line
export function createZeroConflictionLineFeature(ownshipFeature, intruderFeature, zcMessage) {
    const coordinates = getZeroConflictionLineCoordinates(ownshipFeature, intruderFeature);
    const lineString = new LineString(coordinates);

    const feature = new Feature({
        geometry: lineString
    });
    const style = getZeroConflictionLineStyleFromZeroConflictMessage(zcMessage, ownshipFeature);
    feature.setStyle(style);

    const id = `${ownshipFeature.getId()}_${intruderFeature.getId()}`;
    feature.setId(id);

    const ownshipAsdMessage = ownshipFeature.get("asd");
    if (ownshipAsdMessage) {
        feature.set("asd", ownshipAsdMessage);
    }
    return feature;
}

// get coordinates to update the location of the zca line
export function getZeroConflictionLineCoordinates(ownshipFeature, intruderFeature) {
    const ownshipCoords = ownshipFeature.getGeometry().getCoordinates();
    const intruderCoords = intruderFeature.getGeometry().getCoordinates();
    return [ownshipCoords, intruderCoords];
}

// gets style for the line
export function getZeroConflictionLineStyleFromZeroConflictMessage(zcMessage, ownshipFeature) {
    let colorRGB = "255, 235, 0";
    if (zcMessage.severity === "WARNING") {
        colorRGB = "200, 0, 0";
    }
    const ownshipStyle = ownshipFeature.getStyle();
    if (Array.isArray(ownshipStyle)) {
        const firstStyle = ownshipStyle[0];
        if (firstStyle && firstStyle.getImage()) {
            return new Style({
                stroke: new Stroke({ color: `rgba(${colorRGB}, .8)`, width: 2 })
            });
        }
    }
    return new Style({ stroke: undefined });
}

export function updateZeroConflictLineFeature(map, feature) {
    const zcAlerts = feature.get("zero_confliction_alerts") || [];
    zcAlerts.forEach((alert) => {
        const ownshipID = alert.ownship_track.id;
        const ownshipFeature = GetFeature(map, "asd", ownshipID);

        const intruderID = alert.intruder_track.id;
        const intruderFeature = GetFeature(map, "asd", intruderID);

        const zcLineID = `${ownshipID}_${intruderID}`;
        const zcLineFeature = GetFeature(map, "asd", zcLineID);

        if (zcLineFeature && intruderFeature && ownshipFeature) {
            const coordinates = getZeroConflictionLineCoordinates(ownshipFeature, intruderFeature);
            zcLineFeature.getGeometry().setCoordinates(coordinates);

            const ownshipAsdMessage = ownshipFeature.get("asd");
            if (ownshipAsdMessage) {
                zcLineFeature.set("asd", ownshipAsdMessage);
            }
        }
    });
}

export function getBorderFromMapLayerOption(preference) {
    switch (preference.title) {
        case "Airspaces":
            return "2px dotted #9e6e9d";
        case "Facility Maps":
            return "2px solid #90caf9";
        case "Stadiums":
            return "2px solid #00ff00";
        case "National Security UAS Flight Restrictions":
            return "2px dotted #d8abc6";
        case "Alert Areas":
            return "2px solid #0388bf";
        case "Military Operation Areas":
            return "2px dotted #9d6b9e";
        case "Prohibited Airspaces":
            return "2px dotted #0388bf";
        case "Restricted Airspaces":
            return "2px dotted #0388bf";
        case "Warning Areas":
            return "2px dotted #0388bf";
        case "Temporary Flight Restrictions":
            return "2px solid #ff0000";
        case "Airports":
            return "";
        case "DC FRZ SFRA":
            return "2px solid #ffa500";
        default:
            return "3px solid white";
    }
}

export function getSourceName(sensorID, sourceData) {
    let sourceName = "Unknown";

    if (sourceData) {
        if (sourceData.get(sensorID)) sourceName = sourceData.get(sensorID);
        else if (sensorID) sourceName = sensorID;
    } else {
        sourceName = sensorID;
    }

    return sourceName;
}

export function getWaypointFeatureStyleFromFeatureAndSettings(feature, settings) {
    const planningSettings = settings.op_state_settings.find((op) => {
        return op.state === "PLANNING";
    });
    let color = Colors.blue4;
    if (planningSettings) {
        color = planningSettings.color;
    }
    const strokeBlack = new Stroke({ color: Colors.black });
    const fillBlack = new Fill({ color: Colors.black });

    const stroke = new Stroke({ color: color });
    const fill = new Fill({ color: color });

    const styles = [];
    const featureStyle = new Style({
        fill: fillBlack,
        stroke: stroke
    });
    styles.push(featureStyle);

    const coordinates = feature.getGeometry().getCoordinates();
    coordinates.forEach((coordinate, index) => {
        const pointStyle = new Style({
            image: new CircleStyle({
                fill: fill,
                stroke: strokeBlack,
                radius: 12
            }),
            text: new Text({
                text: `${index + 1}`,
                font: "12px Arial",
                fill: fillBlack,
                stroke: strokeBlack
            }),
            geometry: new Point(coordinate)
        });
        styles.push(pointStyle);
    });
    return styles;
}

export function getUpdatedWaypointsFromCoordinatesAndDefaultAltitude(updatedCoords, prevCoords, defaultAltitude) {
    const updated = updatedCoords.map(([lng, lat], i) => {
        let previous = undefined;
        if (updatedCoords.length > prevCoords.length) {
            previous = prevCoords.find((coordinate) => {
                return coordinate.lat === lat && coordinate.lng === lng;
            });
        } else if (updatedCoords.length === prevCoords.length) {
            previous = prevCoords[i];
        }
        if (previous) {
            return { lng: lng, lat: lat, alt_agl_ft: previous.alt_agl_ft };
        } else {
            return { lng: lng, lat: lat, alt_agl_ft: defaultAltitude };
        }
    });
    return updated;
}

export function getGeofenceCoordinatesFromCoordinatesTypeAndBuffer(coordinates, type, horzBufferFt) {
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
        console.error("Invalid waypoints: must be an array of at least two [lon, lat] pairs.");
        return null;
    }

    let geojson = null;
    switch (type) {
        case "CIRCLE": {
            const circleCenter = center(points(coordinates));
            const radiusFt = coordinates.reduce((farthest, coord) => Math.max(farthest, distance(circleCenter, point(coord), { units: "feet" })), 0);
            geojson = circle(circleCenter, radiusFt, { units: "feet" });
            break;
        }
        case "POLYGON":
            geojson = coordinates.length > 2 ? convex(points(coordinates)) : lineString(coordinates);
            break;
        case "CORRIDOR":
            geojson = lineString(coordinates);
            break;
    }

    if (geojson) {
        const buffered = turfBuffer(geojson, Math.max(horzBufferFt, 1), { units: "feet" });
        return buffered.geometry.coordinates;
    } else {
        return null;
    }
}

export function getMapTileOptionIconFromOptionType(type) {
    switch (type) {
        case "LIGHT":
            return "url(/images/LightStreetViewIcon.png)";
        case "DARK":
            return "url(/images/DarkStreetViewIcon.png)";
        case "SATELLITE":
            return "url(/images/TerrainIcon.png)";
        case "SATELLITE_WITH_LABELS":
            return "url(/images/TerrainLabelsIcon.png)";
        case "VFR_SECTIONAL":
            return "url(/images/VfrMapTileIcon.png)";
        default:
            return "url(/images/LightStreetViewIcon.png)";
    }
}

export function getFlightCardsSortTooltipFromSortValue(sortValue) {
    switch (sortValue) {
        case "TIME_START_ASC":
            return "Sorting by start time (asc)";
        case "TIME_START_DESC":
            return "Sorting by start time (desc)";
        case "TIME_END_ASC":
            return "Sorting by end time (asc)";
        case "TIME_END_DESC":
            return "Sorting by end time (desc)";
        case "NAME_A_Z":
            return "Sorting by name a-z";
        case "NAME_Z_A":
            return "Sorting by name z-a";
    }
}

export function getTaggedVehiclesSortTooltipFromSortValue(sortValue) {
    switch (sortValue) {
        case "LAST_UPDATED_ASC":
            return "Sorting by last update (asc)";
        case "LAST_UPDATED_DESC":
            return "Sorting by last update (desc)";
        case "CALLSIGN_A_Z":
            return "Sorting by callsign a-z";
        case "CALLSIGN_Z_A":
            return "Sorting by callsign z-a";
    }
}

export function getUpdatedZeroConflictionAlertsFromZeroConflictMessage(prevMessages, newMessage) {
    const updatedZeroConflictAlerts = new Map(prevMessages);
    const ownshipId = newMessage.ownship_track.id;
    const previousZeroConflictAlerts = updatedZeroConflictAlerts.get(ownshipId) || [];

    if (newMessage.state === "ACTIVE") {
        if (previousZeroConflictAlerts.length > 0) {
            const prevZeroConflictAlertIndex = previousZeroConflictAlerts.findIndex((alert) => {
                return alert.alert_uuid === newMessage.alert_uuid;
            });
            if (prevZeroConflictAlertIndex !== -1) {
                previousZeroConflictAlerts[prevZeroConflictAlertIndex] = newMessage;
                updatedZeroConflictAlerts.set(ownshipId, previousZeroConflictAlerts);
            } else {
                updatedZeroConflictAlerts.set(ownshipId, [...previousZeroConflictAlerts, newMessage]);
            }
        } else {
            updatedZeroConflictAlerts.set(ownshipId, [newMessage]);
        }
    } else if (newMessage.state === "EXPIRED") {
        if (previousZeroConflictAlerts.length > 0) {
            const filteredZeroConflictAlerts = previousZeroConflictAlerts.filter((alert) => {
                return alert.alert_uuid !== newMessage.alert_uuid;
            });
            if (filteredZeroConflictAlerts.length > 0) {
                updatedZeroConflictAlerts.set(ownshipId, filteredZeroConflictAlerts);
            } else {
                updatedZeroConflictAlerts.delete(ownshipId);
            }
        }
    }
    return updatedZeroConflictAlerts;
}

export function getIsNewZeroConflictionAlert(prevMessages, newMessage) {
    const previousZeroConflictAlerts = new Map(prevMessages);
    const ownshipId = newMessage.ownship_track.id;

    if (newMessage.state !== "ACTIVE") return false;

    const previousAlerts = previousZeroConflictAlerts.get(ownshipId) || [];

    return !previousAlerts.some((alert) => alert.alert_uuid === newMessage.alert_uuid);
}

export function getIsHigherSeverityZeroConflictionAlert(prevMessages, newMessage) {
    const previousZeroConflictAlerts = new Map(prevMessages);
    const ownshipId = newMessage.ownship_track.id;

    if (newMessage.state !== "ACTIVE") return false;

    const zeroConflictAlerts = previousZeroConflictAlerts.get(ownshipId) || [];

    return zeroConflictAlerts.some((alert) => alert.alert_uuid === newMessage.alert_uuid && alert.severity === "CAUTION" && newMessage.severity === "WARNING");
}

export function getFeatureTypeFromFeature(feature) {
    const properties = feature.getProperties();
    if ("asd" in properties) {
        return "ASD Track";
    } else if ("flight" in properties) {
        return "Flight";
    } else if ("constraint" in properties) {
        return "Constraint";
    } else if ("alert_volume" in properties) {
        return "Alert Volume";
    } else if ("coa_volume" in properties) {
        return "COA Volume";
    } else if ("sensor" in properties) {
        return "Radar Footprint";
    } else if ("laanc_volume" in properties) {
        return "LAANC Volume";
    } else if ("hms" in properties) {
        return "HIMS System";
    } else if ("hms_component" in properties) {
        return "HIMS Component";
    } else if ("layer" in properties) {
        switch (properties.layer) {
            case "LAANC_AIRSPACE":
                return "Airspace";
            case "UASFM":
                return "UAS Facility Map";
            case "SEAMS_STADIUMS":
            case "LAANC_STADIUMS":
                return "Stadium";
            case "FT_NSUFR":
            case "PT_NSUFR":
                return "National Security UAS Flight Restriction";
            case "SU_AIRSPACE":
                return "Special Use Airspace";
            case "FAA_TFR":
                return "Temporary Flight Restriction";
            case "AIRPORTS":
                return "Airport";
            case "DC_FRZ_SFRA":
                return "Special Flight Rules Area";
        }
    } else {
        return "";
    }
}

export function getAssociatedFlightButtonTooltipFromFeatureAndOperations(feature, operations) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.flight_uuid !== "N/A") {
        const operation = operations.find((op) => op.flight_uuid === properties.asd.flight_uuid);
        if (operation) {
            return operation.name;
        }
    } else {
        return "Not associated with a flight";
    }
}

export function getAssociatedFlightButtonCheckedFromFeatureAndFlightUuid(feature, flightUUID) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.flight_uuid === flightUUID) {
        return true;
    } else {
        return false;
    }
}

export function getAssociatedFlightButtonIconFromFeature(feature) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.flight_uuid !== "N/A") {
        return <AirplanemodeActiveIcon fontSize="small" />;
    } else {
        return <AirplanemodeInactiveIcon fontSize="small" />;
    }
}

export function getZcButtonTooltipFromFeatureAndZcConfigurations(feature, zcConfigs, user) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.daa_configs && properties.asd.daa_configs[user.organization_id]) {
        const zcConfig = zcConfigs.find((config) => {
            return config.config_uuid === properties.asd.daa_configs[user.organization_id];
        });
        if (zcConfig) {
            return zcConfig.name;
        }
    }
    return "Not enrolled in ZC alerting";
}

export function getZcButtonCheckedFromFeatureAndZcConfigurations(feature, user, configUUID) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.daa_configs) {
        const asdConfigUUID = properties.asd.daa_configs[user.organization_id];
        if (asdConfigUUID && asdConfigUUID === configUUID) {
            return true;
        } else if (!asdConfigUUID && !configUUID) {
            return true;
        }
    }
    return false;
}

export function getZcButtonIconFromFeatureAndUser(feature, user) {
    const properties = feature.getProperties();
    if ("asd" in properties && properties.asd.daa_configs) {
        const configUUID = properties.asd.daa_configs[user.organization_id];
        if (configUUID) {
            return <NotificationsIcon fontSize="small" />;
        }
    }
    return <NotificationsOffIcon fontSize="small" />;
}

export function isFeatureSelectable(feature, layer, airspaceLayers) {
    if (!(layer instanceof Layer) || !layer.getVisible()) {
        return false;
    }

    const id = feature.getId();
    if (typeof id === "string" && (id.includes("_pl") || id.includes("_hl") || id.includes("_dpz") || id.includes("_av") || id.includes("_zca"))) {
        return false;
    }
    // zc line should not be selectable
    const layerName = layer.get("name");
    if (layerName === "asd" && typeof id === "string" && id.includes("_")) {
        return false;
    }

    // check sublayers for airspaces
    if (layerName === "LAANC_AIRSPACE") {
        const setting = airspaceLayers.find((sublayer) => sublayer.local_type === feature.get("LOCAL_TYPE"));
        if (!setting || !setting.visible) {
            return false;
        }
    }

    // unexepected layers should not be selectable
    const validLayerNames = [
        "hms_components",
        "hims",
        "asd",
        "flights",
        "constraints",
        "alerts",
        "coa_volumes",
        "sensors",
        "laanc_volumes",
        "planned_flights",
        "published_flights",
        "alert_volumes",
        "LAANC_AIRSPACE",
        "UASFM",
        "STADIUMS",
        "FT_NSUFR",
        "PT_NSUFR",
        "SU_AIRSPACE",
        "FAA_TFR",
        "AIRPORTS",
        "DC_FRZ_SFRA",
        "SEAMS_STADIUMS",
        "externalFlights"
    ];
    return validLayerNames.includes(layerName);
}

// function to return a human readable name for each feature
export function getFeatureTitleFromFeature(feature) {
    const properties = feature.getProperties();
    switch (properties.layer) {
        case "UASFM":
            return `UAS Facility Map`;
        case "FT_NSUFR":
        case "PT_NSUFR":
            return "National Security UAS Flight Restriction";
        case "FAA_TFR":
            return "Temporary Flight Restriction";
        case "SEAMS_STADIUMS":
            return properties.venue;
        default:
            return feature.get("name") || feature.get("NAME");
    }
}

// function which returns an array of fields to display to the user based on the a
export function getFeatureInfoFromFeature(feature) {
    const properties = feature.getProperties();
    if ("asd" in properties) {
        const latDeg = isNaN(properties.asd.lat_deg) ? "--" : properties.asd.lat_deg.toFixed(5);
        const lonDeg = isNaN(properties.asd.lon_deg) ? "--" : properties.asd.lon_deg.toFixed(5);
        const info = [
            { title: "Callsign: ", value: properties.asd.call_sign },
            { title: "Track Angle: ", value: `${properties.asd.course_deg}°` },
            { title: "Position: ", value: `( ${latDeg}, ${lonDeg}  )` },
            { title: "Altitude (HAE): ", value: isNaN(properties.asd.alt_hae_ft) ? `NAR` : `${properties.asd.alt_hae_ft}ft` },
            { title: "Ground Speed: ", value: `${properties.asd.ground_speed_kn}kn` },
            { title: "Source Type: ", value: properties.asd.source_type },
            { title: "Classification: ", value: properties.asd.classification },
            { title: "Source Name: ", value: properties.asd.source_name || properties.asd.source_id },
            { title: "Squawk Code: ", value: properties.asd.squawk || "None" },
            { title: "Last Update: ", value: ConvertISOToDate(properties.asd.timestamp) }
        ];
        if (properties.asd.coasted) {
            info.push({ title: "COASTED", value: "", color: "rgba(200, 200, 0, 0.8)" });
        }
        if (properties.asd.eot) {
            info.push({ title: "EOT", value: "", color: "rgba(200, 0, 0, 0.8)" });
        }
        if (properties.asd.reinforced_flag) {
            info.push({ title: "REINFORCED", value: "", color: "rgba(0, 200, 0, 0.8)" });
        }
        return info;
    } else if ("flight" in properties) {
        return [
            { title: "State: ", value: properties.flight.state },
            { title: "Priority: ", value: `${getOperationPriority(properties.flight)}` },
            { title: "Altitude Min (AGL): ", value: `${ConvertMetersToFeet(properties.flight.volumes[0].altitude_min_agl_m)}ft` },
            { title: "Altitude Max (AGL): ", value: `${ConvertMetersToFeet(properties.flight.volumes[0].altitude_max_agl_m)}ft` },
            { title: "Time Start: ", value: `${ConvertISOToDate(properties.flight.time_start)}` },
            { title: "Time End: ", value: `${ConvertISOToDate(properties.flight.time_end)}` }
        ];
    } else if ("constraint" in properties) {
        return [
            { title: "State: ", value: properties.constraint.state },
            { title: "Type: ", value: `${properties.constraint.type}` },
            { title: "Altitude (AGL): ", value: `${ConvertMetersToFeet(properties.constraint.volumes[0].altitude_max_agl_m)}ft` },
            { title: "Time Start: ", value: `${ConvertISOToDate(properties.constraint.time_start)}` },
            { title: "Time End: ", value: `${ConvertISOToDate(properties.constraint.time_end)}` }
        ];
    } else if ("alert_volume" in properties) {
        return [
            { title: "Altitude Min (HAE): ", value: `${ConvertMetersToFeet(properties.alert_volume.altitude_min_hae_m)}ft` },
            { title: "Altitude Max (HAE): ", value: `${ConvertMetersToFeet(properties.alert_volume.altitude_max_hae_m)}ft` },
            { title: "Altitude Min (AGL): ", value: `${ConvertMetersToFeet(properties.alert_volume.altitude_min_agl_m)}ft` },
            { title: "Altitude Max (AGL): ", value: `${ConvertMetersToFeet(properties.alert_volume.altitude_max_agl_m)}ft` },
            { title: "Time Start: ", value: `${ConvertISOToDate(properties.alert_volume.time_start)}` },
            { title: "Time End: ", value: properties.alert_volume.permanent ? "Permanent" : ConvertISOToDate(properties.alert_volume.time_end) },
            { title: "Date Created: ", value: `${ConvertISOToDate(properties.alert_volume.date_created)}` }
        ];
    } else if ("coa_volume" in properties) {
        return [
            { title: "Altitude (AGL): ", value: `${ConvertMetersToFeet(properties.coa_volume.altitude_agl_m)}ft` },
            { title: "Date Created: ", value: `${ConvertISOToDate(properties.coa_volume.date_created)}` }
        ];
    } else if ("sensor" in properties) {
        return [
            { title: "Sensor ID: ", value: `${properties.sensor.truth_source}` },
            { title: "Sensor Type: ", value: `${properties.sensor.sensor_type}` }
        ];
    } else if ("hms" in properties) {
        return [
            { title: "Status Area", value: "" },
            { title: "Status: ", value: properties.hms.status_type },
            { title: "Date Reported", value: `${ConvertISOToDate(properties.hms.mapSystem.date_reported)}` }
        ];
    } else if ("hms_component" in properties) {
        return [
            { title: "Name: ", value: properties.hms_component.name },
            { title: "State: ", value: properties.hms_component.sensor_state },
            { title: "Fault State: ", value: properties.hms_component.fault_state },
            { title: "Latitude: ", value: properties.hms_component.lat.toFixed(5) },
            { title: "Longitude: ", value: properties.hms_component.lon.toFixed(5) },
            { title: "Radius: ", value: `${properties.hms_component.radius}m` },
            { title: "Type: ", value: properties.hms_component.sensor_type },
            { title: "Systems: ", value: properties.hms_component.systems?.join(", ") || "None" }
        ];
    } else if ("laanc_volume" in properties) {
        const info = [
            { title: "LAANC Ref Code: ", value: properties.laanc_volume.id },
            { title: "Submission State: ", value: `${properties.laanc_volume.faa_request_type.toUpperCase().replace(/_/g, " ")}` },
            { title: "LAANC Volume State: ", value: `${properties.laanc_volume.state.toUpperCase().replace(/_/g, " ")}` },
            { title: "Altitude (AGL): ", value: `${ConvertMetersToFeet(properties.laanc_volume.altitude_max_agl_m)}ft` },
            { title: "Facility Code: ", value: `${properties.laanc_volume.airport}` },
            { title: "Time Start: ", value: `${ConvertISOToDate(properties.laanc_volume.time_start)}` },
            { title: "Time End: ", value: `${ConvertISOToDate(properties.laanc_volume.time_end)}` }
        ];
        if (properties.laanc_volume.advisories) {
            properties.laanc_volume.advisories.forEach((advisory) => {
                info.push({ title: advisory.type + ": ", value: `${advisory.msg}` });
            });
        }
        return info;
    } else if ("layer" in properties) {
        switch (properties.layer) {
            case "LAANC_AIRSPACE":
                return [{ title: "Local Type: ", value: `${properties.LOCAL_TYPE}` }];
            case "UASFM":
                return [
                    { title: "Ceiling: ", value: `${properties.CEILING} feet` },
                    { title: "Last edit: ", value: `${properties.LAST_EDIT}` },
                    { title: "Globalid: ", value: `${properties.GLOBALID}` },
                    { title: "Apt1 LAANC: ", value: `${properties.APT1_LAANC}` }
                ];
            case "LAANC_STADIUMS":
                return [
                    { title: "City: ", value: `${properties.CITY}` },
                    { title: "State: ", value: `${properties.STATE}` },
                    { title: "Status code: ", value: `${properties.STATUS_CODE}` }
                ];
            case "FT_NSUFR":
            case "PT_NSUFR":
                return [
                    { title: "Proponent: ", value: `${properties.Proponent}` },
                    { title: "Branch: ", value: `${properties.Branch}` },
                    { title: "Base: ", value: `${properties.Base}` },
                    { title: "Facility: ", value: `${properties.Facility}` },
                    { title: "Airspace: ", value: `${properties.Airspace}` },
                    { title: "Reason: ", value: `${properties.Reason}` },
                    { title: "State: ", value: `${properties.State}` },
                    { title: "Floor: ", value: `${properties.Floor}` },
                    { title: "Ceiling: ", value: `${properties.Ceiling}` }
                ];
            case "SU_AIRSPACE":
                return [{ title: "Type code: ", value: `${properties.TYPE_CODE}` }];
            case "FAA_TFR":
                return [
                    { title: "Number: ", value: `${properties.number}` },
                    { title: "Issued: ", value: `${ConvertISOToDate(properties.issued)}` },
                    { title: "Effective Start: ", value: `${ConvertISOToDate(properties.effectiveStart)}` },
                    { title: "Effective End: ", value: `${ConvertISOToDate(properties.effectiveEnd)}` },
                    { title: "URL: ", link: `${properties.tfr_url}` }
                ];
            case "AIRPORTS":
                return [
                    { title: "Ident: ", value: `${properties.IDENT}` },
                    { title: "Type code: ", value: `${properties.TYPE_CODE}` },
                    { title: "Servcity: ", value: `${properties.SERVCITY}` },
                    { title: "State: ", value: `${properties.STATE}` },
                    { title: "Operstatus: ", value: `${properties.OPERSTATUS}` },
                    { title: "Mil code: ", value: `${properties.MIL_CODE}` },
                    { title: "Airanal: ", value: `${properties.AIRANAL}` }
                ];
            case "DC_FRZ_SFRA":
                return [{ title: "Sector: ", value: `${properties.SECTOR}` }];
            case "SEAMS_STADIUMS": {
                try {
                    const events = JSON.parse(properties.events);
                    const allEvents = events.map((event) => {
                        return { title: event.EVENT_NAME, value: formatEventDateTimeRange(event.TFR_START, event.TFR_END) };
                    });
                    return allEvents.length < 1 ? [{ title: "No upcoming events.", value: "" }] : allEvents;
                } catch (err) {
                    return [{ title: "An error occured while parsing this stadiums events.", value: "" }];
                }
            }
        }
    } else if ("externalFlight" in properties) {
        return [
            { title: "State: ", value: properties.externalFlight.state },
            { title: "Priority: ", value: `${getOperationPriority(properties.externalFlight)}` },
            { title: "Altitude Min (HAE): ", value: `${ConvertMetersToFeet(properties.externalFlight.altitude_min_hae_m)}ft` },
            { title: "Altitude Max (HAE): ", value: `${ConvertMetersToFeet(properties.externalFlight.altitude_max_hae_m)}ft` },
            { title: "Time Start: ", value: `${ConvertISOToDate(properties.externalFlight.time_start)}` },
            { title: "Time End: ", value: `${ConvertISOToDate(properties.externalFlight.time_end)}` }
        ];
    }
}

export function getBaseMapTileSourceFromOption(option) {
    switch (option.type) {
        case "TILE_JSON":
            return new TileJSON({ url: `${option.source}`, crossOrigin: "anonymous", tileSize: 512 });
        case "BING_MAPS":
            return new BingMaps({ key: option.key, imagerySet: option.source });
        case "XYZ":
            return new XYZ({ url: option.source, tileSize: 512 });
        case "GOOGLE":
            return new Google({ key: option.key, mapType: option.source });
        case "GOOGLE_LABELS":
            return new Google({ key: option.key, mapType: option.source, layerTypes: ["layerRoadmap"] });
        case "ARCGIS":
            return new TileArcGISRest({ url: option.source });
        default:
            return undefined;
    }
}

export function getBaseMapTileStyleUrlFromOption(option) {
    if (option.type === "VECTOR_BASEMAP") {
        return option.source;
    } else {
        return undefined;
    }
}

export function getBaseMapTileOptionFromOption(option, i) {
    return {
        index: i,
        title: option.name,
        url: getMapTileOptionIconFromOptionType(option.icon),
        source: getBaseMapTileSourceFromOption(option),
        style_url: getBaseMapTileStyleUrlFromOption(option)
    };
}

export function formatEventDateTimeRange(eventStart, eventEnd) {
    const start = dayjs.utc(eventStart);
    const end = dayjs.utc(eventEnd);

    const timeFormat = "h:mm A [UTC]";
    const dateFormat = "MMM D, YYYY";

    if (start.isSame(end, "day")) {
        return `${start.format(dateFormat)}, ${start.format(timeFormat)} - ${end.format(timeFormat)}`;
    } else {
        return `${start.format(dateFormat)}, ${start.format(timeFormat)} - ${end.format(dateFormat)}, ${end.format(timeFormat)}`;
    }
}

export async function getTileserverStatuses() {
    return new Promise((resolve, reject) => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("api/tileserver/statuses/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => resolve(data))
            .catch((err) => reject(err));
    });
}

export function hasTaggedCallsign(feature, user) {
    const asd = feature.get("asd");
    if (!asd) return false;

    if (asd.tagged_callsign && asd.tagged_callsign[user.organization_id] && asd.tagged_callsign[user.organization_id] !== asd.label) {
        return true;
    } else {
        return false;
    }
}

export function getTaggedCallsign(asd, user) {
    if (!asd || !user) {
        return "";
    }
    if (asd.tagged_callsign && asd.tagged_callsign[user.organization_id]) {
        return asd.tagged_callsign[user.organization_id];
    } else {
        return asd.label;
    }
}

export function getHomeLocationFeature(settings) {
    const homeLocationFeature = new Feature({
        geometry: new Point([settings.longitude, settings.latitude])
    });
    const homeLocationStyle = new Style({
        image: new Icon({
            src: "../images/PlaceIcon.svg",
            anchor: [0.5, 1]
        }),
        fill: new Fill({ color: "#ffffff" })
    });
    homeLocationFeature.setStyle(homeLocationStyle);

    return homeLocationFeature;
}

export function getHasHigherPriorityZcAlert(previousZcAlerts, newZcAlerts) {
    return newZcAlerts.some((current) => {
        const previous = previousZcAlerts.find((p) => {
            return p.alert_uuid === current.alert_uuid;
        });
        if (previous) {
            return previous.severity === "CAUTION" && current.severity === "WARNING";
        } else {
            return false;
        }
    });
}

export function shouldHideAsd(asd, user) {
    const isCalAdmin = user.organization_id === 1 && user.user_role === "Admin";
    if (isCalAdmin || !asd.organization_id) {
        return false;
    }

    const isTelemetry = asd.source_type === "TELEMETRY";
    const sameOrg = asd.organization_id === user.organization_id;
    return isTelemetry && !sameOrg;
}
